/**
 * Which keyboard the reader has: Mac or PC.
 *
 * Shortcut hints must name the keys the reader actually presses — "⌘ Return"
 * on a Mac, "Ctrl Enter" everywhere else — and the handler must listen for the
 * same modifier the hint names. Both read this one answer.
 *
 * ## Where the answer comes from, in order
 *
 * 1. `navigator.userAgentData.platform` — the User-Agent Client Hints value
 *    ("macOS", "Windows", …). Chromium only, and the one source that is not
 *    frozen or spoofed for compatibility.
 * 2. `navigator.platform` — deprecated but still populated everywhere
 *    ("MacIntel", "Win32", …).
 * 3. `navigator.userAgent` — the last resort ("Macintosh; Intel Mac OS X").
 *
 * An iPad reports a Mac user agent; it is treated as a Mac, which is right:
 * its hardware keyboard has ⌘.
 */

export interface PlatformNavigator {
  userAgentData?: { platform?: string } | null;
  platform?: string;
  userAgent?: string;
}

const APPLE = /mac|iphone|ipad|ipod/i;

export function isMacPlatform(nav: PlatformNavigator | undefined = globalThis.navigator): boolean {
  if (!nav) return false;
  const hinted = nav.userAgentData?.platform;
  if (hinted) return APPLE.test(hinted);
  if (nav.platform) return APPLE.test(nav.platform);
  return APPLE.test(nav.userAgent ?? "");
}

/** The two keys of a "modifier + Enter" shortcut, named the way the reader's
 *  own keyboard labels them. */
export interface ShortcutKeys {
  /** Shown on the key cap: "⌘" or "Ctrl". */
  mod: string;
  /** Shown on the key cap: "Return" or "Enter". */
  enter: string;
  /** Whether a keyboard event carries this platform's modifier. */
  hasMod: (event: Pick<KeyboardEvent, "metaKey" | "ctrlKey">) => boolean;
}

export function shortcutKeys(mac: boolean = isMacPlatform()): ShortcutKeys {
  return mac
    ? { mod: "⌘", enter: "Return", hasMod: (event) => event.metaKey }
    : { mod: "Ctrl", enter: "Enter", hasMod: (event) => event.ctrlKey };
}
